import { useCallback, useEffect, useMemo, useState } from "react";
import jsonExport from "jsonexport/dist";
import {
  FileText,
  Send,
  CheckCircle2,
  Clock,
  AlertTriangle,
  User,
  RefreshCw,
  Download,
  Mail,
  XCircle,
  Percent,
  Ban,
} from "lucide-react";
import { downloadCSV, useCanAccess, useDataProvider, useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  blobToBase64,
  buildSimplePdfBlob,
  downloadSimplePdf,
  formatPriceInINR,
  formatPriceInINRAscii,
  type PdfLine,
} from "@/lib/formatters";
import { sendDemandEmail } from "@/lib/whatsapp";
import { useRealEstateContext } from "../providers/RealEstateContext";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type {
  Booking,
  ChannelPartner,
  DiscountStatus,
  PaymentSchedule,
  PayoutStatus,
} from "@/types/realEstate";
import {
  computeCancellationRefund,
  computeLateInterest,
  isUuid,
  normalizeLateInterestAnnualPercent,
} from "./bookingUtils";

interface MilestoneBooking {
  id: string;
  booking_date: string;
  unit_number: string;
  project_name: string;
  buyer_name: string;
  buyer_phone: string;
  buyer_email: string;
  total_agreement_value: number;
  list_price?: number | null;
  discount_amount?: number;
  discount_status?: DiscountStatus;
  cp_commission_amount?: number;
  agent_commission_amount?: number;
  cp_payout_status?: PayoutStatus;
  agent_payout_status?: PayoutStatus;
  cp_agency?: string;
  cp_percentage?: number;
  agent_name?: string;
  documents_verified?: boolean;
  milestones: PaymentSchedule[];
}

const getDiscountBadge = (status: DiscountStatus) => {
  switch (status) {
    case "pending":
      return (
        <Badge className="bg-amber-500 text-white">
          <Percent className="w-3 h-3 mr-1" /> Discount pending
        </Badge>
      );
    case "approved":
      return (
        <Badge className="bg-emerald-600 text-white">
          <CheckCircle2 className="w-3 h-3 mr-1" /> Discount approved
        </Badge>
      );
    case "rejected":
      return (
        <Badge className="bg-red-600 text-white">
          <XCircle className="w-3 h-3 mr-1" /> Discount rejected
        </Badge>
      );
    default:
      return null;
  }
};

const getMilestoneBadge = (status: PaymentSchedule["status"]) => {
  switch (status) {
    case "PAID":
      return (
        <Badge className="bg-emerald-600 text-white">
          <CheckCircle2 className="w-3 h-3 mr-1" /> Paid
        </Badge>
      );
    case "DEMAND_SENT":
      return (
        <Badge className="bg-indigo-600 text-white">
          <Send className="w-3 h-3 mr-1" /> Demand Sent
        </Badge>
      );
    case "OVERDUE":
      return (
        <Badge className="bg-red-600 text-white">
          <AlertTriangle className="w-3 h-3 mr-1" /> Overdue
        </Badge>
      );
    default:
      return (
        <Badge variant="outline">
          <Clock className="w-3 h-3 mr-1" /> Pending
        </Badge>
      );
  }
};

const milestoneOutstanding = (milestone: PaymentSchedule): number =>
  Math.max(
    0,
    (Number(milestone.amount_due) || 0) - (Number(milestone.amount_paid) || 0),
  );

const isDispatchableMilestone = (milestone: PaymentSchedule): boolean =>
  milestone.status !== "PAID" && milestone.status !== "CANCELLED";

const isOverdueMilestone = (
  milestone: PaymentSchedule,
  asOf = new Date(),
): boolean => {
  if (!isDispatchableMilestone(milestone)) return false;
  if (milestone.status === "OVERDUE") return true;
  if (!milestone.due_date) return false;
  const due = new Date(milestone.due_date);
  if (Number.isNaN(due.getTime())) return false;
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const asOfDay = new Date(asOf.getFullYear(), asOf.getMonth(), asOf.getDate());
  return asOfDay.getTime() > dueDay.getTime();
};

const formatDueDate = (dueDate?: string | null): string => {
  if (!dueDate) return "—";
  const d = new Date(dueDate);
  if (Number.isNaN(d.getTime())) return dueDate;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/** Branded payment-demand letter content (ASCII for Helvetica PDF). */
export const demandLetterLines = (
  booking: MilestoneBooking,
  milestone: PaymentSchedule,
  lateInterestAnnualPercent: number,
  brandTitle = "Blivix CRM",
): PdfLine[] => {
  const outstanding = milestoneOutstanding(milestone);
  const interest = computeLateInterest({
    outstanding,
    annualPercent: lateInterestAnnualPercent,
    dueDate: milestone.due_date,
  });
  const paidTotal = booking.milestones.reduce(
    (sum, m) => sum + (Number(m.amount_paid) || 0),
    0,
  );
  const dueTotal = booking.milestones.reduce(
    (sum, m) => sum + (Number(m.amount_due) || 0),
    0,
  );

  const lines: PdfLine[] = [
    { kind: "title", text: brandTitle },
    { kind: "subtitle", text: "PAYMENT DEMAND NOTICE" },
    { kind: "rule" },
    {
      kind: "muted",
      text: `Date: ${new Date().toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })}`,
    },
    { kind: "spacer" },
    { kind: "heading", text: "Buyer" },
    { kind: "body", text: booking.buyer_name },
  ];
  if (booking.buyer_phone) {
    lines.push({ kind: "body", text: `Phone: ${booking.buyer_phone}` });
  }
  if (booking.buyer_email) {
    lines.push({ kind: "body", text: `Email: ${booking.buyer_email}` });
  }

  lines.push(
    { kind: "spacer" },
    { kind: "heading", text: "Unit & Project" },
    { kind: "body", text: `Unit: ${booking.unit_number}` },
    { kind: "body", text: `Project: ${booking.project_name}` },
    {
      kind: "body",
      text: `Booking date: ${booking.booking_date ? formatDueDate(booking.booking_date) : "—"}`,
    },
    { kind: "spacer" },
    { kind: "heading", text: "Agreement Summary" },
    {
      kind: "body",
      text: `Agreement value: ${formatPriceInINRAscii(booking.total_agreement_value)}`,
    },
  );
  if (booking.list_price != null && Number(booking.list_price) > 0) {
    lines.push({
      kind: "body",
      text: `List price: ${formatPriceInINRAscii(Number(booking.list_price))}`,
    });
  }
  if ((booking.discount_amount || 0) > 0) {
    lines.push({
      kind: "body",
      text: `Discount: ${formatPriceInINRAscii(booking.discount_amount)} (${booking.discount_status || "applied"})`,
    });
  }
  lines.push(
    {
      kind: "body",
      text: `Collected to date: ${formatPriceInINRAscii(paidTotal)} of ${formatPriceInINRAscii(dueTotal)}`,
    },
    { kind: "spacer" },
    { kind: "heading", text: "Demand Milestone" },
    { kind: "body", text: `Milestone: ${milestone.milestone_name}` },
    { kind: "body", text: `Due percentage: ${milestone.due_percentage}%` },
    { kind: "body", text: `Due date: ${formatDueDate(milestone.due_date)}` },
    {
      kind: "body",
      text: `Amount due: ${formatPriceInINRAscii(milestone.amount_due)}`,
    },
    {
      kind: "body",
      text: `Amount paid: ${formatPriceInINRAscii(Number(milestone.amount_paid) || 0)}`,
    },
    {
      kind: "body",
      text: `Outstanding: ${formatPriceInINRAscii(outstanding)}`,
    },
    { kind: "body", text: `Status: ${milestone.status}` },
  );

  if (interest > 0) {
    lines.push(
      { kind: "spacer" },
      { kind: "heading", text: "Late Interest" },
      {
        kind: "body",
        text: `Rate: ${lateInterestAnnualPercent}% p.a. (simple interest on overdue outstanding)`,
      },
      {
        kind: "body",
        text: `Interest accrued: ${formatPriceInINRAscii(interest)}`,
      },
      {
        kind: "subtitle",
        text: `Total payable: ${formatPriceInINRAscii(outstanding + interest)}`,
      },
    );
  } else {
    lines.push(
      { kind: "spacer" },
      {
        kind: "subtitle",
        text: `Amount payable: ${formatPriceInINRAscii(outstanding > 0 ? outstanding : milestone.amount_due)}`,
      },
    );
  }

  const scheduleOpen = booking.milestones.filter(isDispatchableMilestone);
  if (scheduleOpen.length > 0) {
    lines.push(
      { kind: "spacer" },
      { kind: "heading", text: "Payment Schedule (open)" },
    );
    for (const m of scheduleOpen) {
      const mOut = milestoneOutstanding(m);
      lines.push({
        kind: "muted",
        text: `${m.milestone_name}  |  ${m.due_percentage}%  |  due ${formatDueDate(m.due_date)}  |  ${formatPriceInINRAscii(mOut)}  |  ${m.status}`,
      });
    }
  }

  lines.push(
    { kind: "rule" },
    {
      kind: "body",
      text: "Please arrange payment for the above milestone and confirm once done.",
    },
    {
      kind: "muted",
      text: `This notice was generated from ${brandTitle} post-sales.`,
    },
  );
  return lines;
};

export const DemandLetterGenerator = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { lateInterestAnnualPercent: lateInterestRaw, title: crmTitle } =
    useConfigurationContext();
  const brandTitle = crmTitle?.trim() || "Blivix CRM";
  const lateInterestAnnualPercent =
    normalizeLateInterestAnnualPercent(lateInterestRaw);
  const { canAccess: canApproveDiscounts } = useCanAccess({
    resource: "sales",
    action: "edit",
  });
  const {
    triggerWhatsAppMessage,
    createBooking,
    approveBookingDiscount,
    cancelBooking,
    refreshCommissionLedger,
  } = useRealEstateContext();
  const [bookings, setBookings] = useState<MilestoneBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [togglingDocsId, setTogglingDocsId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMilestoneName, setBulkMilestoneName] = useState("");
  const [cancelTarget, setCancelTarget] = useState<MilestoneBooking | null>(
    null,
  );
  const [cancelDeductionPercent, setCancelDeductionPercent] = useState(10);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);

  const pendingDiscountBookings = useMemo(
    () => bookings.filter((b) => b.discount_status === "pending"),
    [bookings],
  );

  const milestoneNames = useMemo(() => {
    const names = new Set<string>();
    for (const b of bookings) {
      for (const m of b.milestones) {
        if (isDispatchableMilestone(m)) names.add(m.milestone_name);
      }
    }
    return [...names];
  }, [bookings]);

  const cancelRefundPreview = useMemo(() => {
    if (!cancelTarget) return null;
    const paid = cancelTarget.milestones.reduce(
      (sum, m) => sum + (Number(m.amount_paid) || 0),
      0,
    );
    return computeCancellationRefund(paid, cancelDeductionPercent);
  }, [cancelTarget, cancelDeductionPercent]);

  const loadBookings = useCallback(async () => {
    setLoading(true);
    try {
      const [bookingRes, cpRes, salesRes] = await Promise.all([
        dataProvider.getList<Booking>("bookings", {
          pagination: { page: 1, perPage: 100 },
          sort: { field: "created_at", order: "DESC" },
          filter: { "cancelled_at@is": null },
        }),
        dataProvider
          .getList<ChannelPartner>("channel_partners", {
            pagination: { page: 1, perPage: 200 },
            sort: { field: "created_at", order: "DESC" },
            filter: {},
          })
          .catch(() => ({ data: [] as ChannelPartner[] })),
        dataProvider
          .getList<{
            id: string | number;
            first_name?: string;
            last_name?: string;
          }>("sales", {
            pagination: { page: 1, perPage: 200 },
            sort: { field: "id", order: "ASC" },
            filter: {},
          })
          .catch(() => ({
            data: [] as Array<{
              id: string | number;
              first_name?: string;
              last_name?: string;
            }>,
          })),
      ]);

      const rows = (bookingRes.data || []).filter((b) => !b.cancelled_at);
      if (rows.length === 0) {
        setBookings([]);
        return;
      }

      const cpById = new Map(
        (cpRes.data || []).map((cp) => [String(cp.id), cp]),
      );
      const salesById = new Map(
        (salesRes.data || []).map((s) => [
          String(s.id),
          `${s.first_name || ""} ${s.last_name || ""}`.trim(),
        ]),
      );

      type ContactRow = {
        id: string | number;
        first_name?: string;
        last_name?: string;
        phone_jsonb?: { number: string }[];
        phone_secondary?: string;
        email_jsonb?: { email: string; type?: string }[];
        channel_partner_id?: string | null;
        sales_id?: string | number | null;
      };
      type PropertyRow = {
        id: string;
        unit_number?: string;
        project?: { name?: string };
      };

      const bookingIds = rows.map((b) => String(b.id));
      const contactIds = [
        ...new Set(
          rows
            .map((b) => b.contact_id)
            .filter((id): id is string | number => id != null)
            .map(String),
        ),
      ];
      const propertyIds = [
        ...new Set(
          rows
            .map((b) => b.property_id)
            .filter((id): id is string => id != null)
            .map(String),
        ),
      ];

      const [schedRes, contactsRes, propsRes] = await Promise.all([
        dataProvider
          .getList<PaymentSchedule>("payment_schedules", {
            pagination: { page: 1, perPage: 5000 },
            sort: { field: "due_percentage", order: "ASC" },
            filter: { "booking_id@in": `(${bookingIds.join(",")})` },
          })
          .catch(() => ({ data: [] as PaymentSchedule[] })),
        contactIds.length > 0
          ? dataProvider
              .getList<ContactRow>("contacts", {
                pagination: { page: 1, perPage: 200 },
                sort: { field: "id", order: "ASC" },
                filter: { "id@in": `(${contactIds.join(",")})` },
              })
              .catch(() => ({ data: [] as ContactRow[] }))
          : Promise.resolve({ data: [] as ContactRow[] }),
        propertyIds.length > 0
          ? dataProvider
              .getList<PropertyRow>("properties", {
                pagination: { page: 1, perPage: 200 },
                sort: { field: "id", order: "ASC" },
                filter: { "id@in": `(${propertyIds.join(",")})` },
              })
              .catch(() => ({ data: [] as PropertyRow[] }))
          : Promise.resolve({ data: [] as PropertyRow[] }),
      ]);

      const schedulesByBookingId = new Map<string, PaymentSchedule[]>();
      for (const schedule of schedRes.data || []) {
        const bookingId = String(schedule.booking_id);
        const list = schedulesByBookingId.get(bookingId) || [];
        list.push(schedule);
        schedulesByBookingId.set(bookingId, list);
      }
      for (const schedules of schedulesByBookingId.values()) {
        schedules.sort(
          (a, b) =>
            (Number(a.due_percentage) || 0) - (Number(b.due_percentage) || 0),
        );
      }

      const contactsById = new Map(
        (contactsRes.data || []).map((c) => [String(c.id), c]),
      );
      const propsById = new Map(
        (propsRes.data || []).map((p) => [String(p.id), p]),
      );

      const enriched: MilestoneBooking[] = rows.map((b) => {
        const contact = b.contact_id
          ? contactsById.get(String(b.contact_id))
          : undefined;
        const prop = b.property_id
          ? propsById.get(String(b.property_id))
          : undefined;
        const buyerName = contact
          ? `${contact.first_name || ""} ${contact.last_name || ""}`.trim() ||
            "Buyer"
          : "Buyer";
        const buyerPhone =
          contact?.phone_jsonb?.[0]?.number || contact?.phone_secondary || "";
        const buyerEmail =
          contact?.email_jsonb?.find((e) => e.email)?.email || "";

        const bookingCpId = b.channel_partner_id
          ? String(b.channel_partner_id)
          : "";
        const contactCpId = contact?.channel_partner_id
          ? String(contact.channel_partner_id)
          : "";
        const cpId = bookingCpId || contactCpId;
        const cp = cpId ? cpById.get(cpId) : undefined;
        const salesId =
          contact?.sales_id != null ? String(contact.sales_id) : "";
        const agentName = salesId ? salesById.get(salesId) || "" : "";
        const snapPct =
          b.cp_percentage != null && Number.isFinite(Number(b.cp_percentage))
            ? Number(b.cp_percentage)
            : undefined;

        return {
          id: String(b.id),
          booking_date: b.booking_date || "",
          unit_number: prop?.unit_number || String(b.property_id || "Unit"),
          project_name: prop?.project?.name || "Project",
          buyer_name: buyerName,
          buyer_phone: buyerPhone,
          buyer_email: buyerEmail,
          total_agreement_value: Number(b.agreement_value) || 0,
          list_price: b.list_price ?? null,
          discount_amount: Number(b.discount_amount) || 0,
          discount_status: b.discount_status,
          cp_commission_amount: Number(b.cp_commission_amount) || 0,
          agent_commission_amount: Number(b.agent_commission_amount) || 0,
          cp_payout_status: b.cp_payout_status,
          agent_payout_status: b.agent_payout_status,
          cp_agency: cp?.agency_name || "",
          cp_percentage: snapPct ?? (Number(cp?.commission_percentage) || 0),
          agent_name: agentName,
          documents_verified: Boolean(b.documents_verified),
          milestones: schedulesByBookingId.get(String(b.id)) || [],
        };
      });

      setBookings(enriched);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to load bookings";
      notify(message, { type: "error" });
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [dataProvider, notify]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const markDemandSent = async (bookingId: string, milestoneId: string) => {
    await dataProvider.update("payment_schedules", {
      id: milestoneId,
      data: { status: "DEMAND_SENT" },
      previousData: { id: milestoneId },
    });

    setBookings((prev) =>
      prev.map((b) => {
        if (b.id !== bookingId) return b;
        return {
          ...b,
          milestones: b.milestones.map((m) =>
            m.id === milestoneId ? { ...m, status: "DEMAND_SENT" as const } : m,
          ),
        };
      }),
    );
  };

  const handleDownloadPdf = (
    booking: MilestoneBooking,
    milestone: PaymentSchedule,
  ) => {
    const filename = `demand-${booking.unit_number}-${milestone.milestone_name}`
      .replace(/[^\w.-]+/g, "_")
      .slice(0, 80);
    downloadSimplePdf(
      filename,
      demandLetterLines(
        booking,
        milestone,
        lateInterestAnnualPercent,
        brandTitle,
      ),
      { brandTitle },
    );
    notify("Demand letter PDF downloaded", { type: "info" });
  };

  const demandAmountLabel = (milestone: PaymentSchedule): string => {
    const outstanding = milestoneOutstanding(milestone);
    const interest = computeLateInterest({
      outstanding,
      annualPercent: lateInterestAnnualPercent,
      dueDate: milestone.due_date,
    });
    if (interest > 0) {
      return `${formatPriceInINR(outstanding + interest)} (incl. interest)`;
    }
    return formatPriceInINR(
      outstanding > 0 ? outstanding : milestone.amount_due,
    );
  };

  const handleEmailDemand = async (
    booking: MilestoneBooking,
    milestone: PaymentSchedule,
  ) => {
    if (!booking.buyer_email) {
      notify("No buyer email on contact — PDF still available via Download", {
        type: "warning",
      });
      return;
    }
    try {
      const lines = demandLetterLines(
        booking,
        milestone,
        lateInterestAnnualPercent,
        brandTitle,
      );
      const pdfBase64 = await blobToBase64(
        buildSimplePdfBlob(lines, { brandTitle }),
      );
      const filename = `demand-${booking.unit_number}.pdf`.replace(
        /[^\w.-]+/g,
        "_",
      );
      const textBody = [
        `Namaste ${booking.buyer_name},`,
        "",
        `Payment demand for milestone "${milestone.milestone_name}" on unit ${booking.unit_number} (${booking.project_name}).`,
        `Amount due: ${demandAmountLabel(milestone)}.`,
        "",
        "Please find the demand letter PDF attached.",
        "Regards,",
        brandTitle,
      ].join("\n");

      const result = await sendDemandEmail({
        to: booking.buyer_email,
        subject: `Payment demand — ${milestone.milestone_name} — ${booking.unit_number}`,
        textBody,
        pdfBase64,
        pdfFilename: filename,
      });

      if (!result.success) {
        notify(result.error || "Email failed", { type: "error" });
        return;
      }

      await markDemandSent(booking.id, milestone.id);
      if (result.status === "simulated") {
        notify(
          "Demand marked DEMAND_SENT — Postmark not configured (email simulated)",
          { type: "warning" },
        );
      } else {
        notify(`Demand emailed to ${booking.buyer_email}`, { type: "success" });
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to email demand";
      notify(message, { type: "error" });
    }
  };

  const dispatchDemandQuiet = async (
    booking: MilestoneBooking,
    milestone: PaymentSchedule,
  ): Promise<{ ok: boolean; error?: string }> => {
    if (booking.buyer_email) {
      const lines = demandLetterLines(
        booking,
        milestone,
        lateInterestAnnualPercent,
        brandTitle,
      );
      const pdfBase64 = await blobToBase64(
        buildSimplePdfBlob(lines, { brandTitle }),
      );
      const result = await sendDemandEmail({
        to: booking.buyer_email,
        subject: `Payment demand — ${milestone.milestone_name} — ${booking.unit_number}`,
        textBody: `Namaste ${booking.buyer_name},\n\nPayment demand for "${milestone.milestone_name}" on ${booking.unit_number}. Amount due: ${demandAmountLabel(milestone)}.\n\nPDF attached.\n\n${brandTitle}`,
        pdfBase64,
        pdfFilename: `demand-${booking.unit_number}.pdf`.replace(
          /[^\w.-]+/g,
          "_",
        ),
      });
      if (!result.success) {
        return { ok: false, error: result.error || "Email failed" };
      }
    }

    if (booking.buyer_phone) {
      const msg = `Namaste ${booking.buyer_name}! Payment demand for milestone "${milestone.milestone_name}". Amount due: ${demandAmountLabel(milestone)}. Please arrange payment and reply once done.`;
      triggerWhatsAppMessage(booking.buyer_phone, msg, true);
    }

    await markDemandSent(booking.id, milestone.id);
    return { ok: true };
  };

  const handleSendDemand = async (
    booking: MilestoneBooking,
    milestone: PaymentSchedule,
  ) => {
    try {
      const result = await dispatchDemandQuiet(booking, milestone);
      if (!result.ok) {
        notify(result.error || "Failed to dispatch demand", { type: "error" });
        return;
      }
      handleDownloadPdf(booking, milestone);
      if (!booking.buyer_phone) {
        notify("Demand marked DEMAND_SENT — no buyer phone for WhatsApp", {
          type: "warning",
        });
      }
      notify("Demand letter dispatched", { type: "success" });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to update milestone";
      notify(message, { type: "error" });
    }
  };

  const handleBulkDispatchOverdue = async () => {
    const targets: Array<{
      booking: MilestoneBooking;
      milestone: PaymentSchedule;
    }> = [];
    for (const booking of bookings) {
      for (const milestone of booking.milestones) {
        if (isOverdueMilestone(milestone)) {
          targets.push({ booking, milestone });
        }
      }
    }
    if (targets.length === 0) {
      notify("No overdue milestones to dispatch", { type: "warning" });
      return;
    }
    setBulkBusy(true);
    let ok = 0;
    let failed = 0;
    try {
      for (const { booking, milestone } of targets) {
        try {
          const result = await dispatchDemandQuiet(booking, milestone);
          if (result.ok) ok += 1;
          else failed += 1;
        } catch {
          failed += 1;
        }
      }
      notify(
        failed > 0
          ? `Dispatched ${ok} of ${targets.length} overdue demand(s); ${failed} failed (email or mark)`
          : `Dispatched ${ok} of ${targets.length} overdue demand(s)`,
        { type: failed === 0 ? "success" : "warning" },
      );
      await loadBookings();
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkDispatchMilestone = async () => {
    const name = bulkMilestoneName.trim();
    if (!name) {
      notify("Select a milestone to dispatch", { type: "warning" });
      return;
    }
    const targets: Array<{
      booking: MilestoneBooking;
      milestone: PaymentSchedule;
    }> = [];
    for (const booking of bookings) {
      for (const milestone of booking.milestones) {
        if (
          milestone.milestone_name === name &&
          isDispatchableMilestone(milestone)
        ) {
          targets.push({ booking, milestone });
        }
      }
    }
    if (targets.length === 0) {
      notify(`No open "${name}" milestones to dispatch`, { type: "warning" });
      return;
    }
    setBulkBusy(true);
    let ok = 0;
    let failed = 0;
    try {
      for (const { booking, milestone } of targets) {
        try {
          const result = await dispatchDemandQuiet(booking, milestone);
          if (result.ok) ok += 1;
          else failed += 1;
        } catch {
          failed += 1;
        }
      }
      notify(
        failed > 0
          ? `Milestone "${name}": dispatched ${ok} of ${targets.length}; ${failed} failed (email or mark)`
          : `Milestone complete — dispatched ${ok} of ${targets.length} "${name}" demand(s)`,
        { type: failed === 0 ? "success" : "warning" },
      );
      await loadBookings();
    } finally {
      setBulkBusy(false);
    }
  };

  const openCancelDialog = (booking: MilestoneBooking) => {
    setCancelTarget(booking);
    setCancelDeductionPercent(10);
    setCancelReason("");
  };

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelBusy(true);
    try {
      const result = await cancelBooking(cancelTarget.id, {
        deductionPercent: cancelDeductionPercent,
        reason: cancelReason,
      });
      if (!result.ok) {
        notify(result.error || "Could not cancel booking", { type: "error" });
        return;
      }
      notify(
        `Booking cancelled. Refund ${formatPriceInINR(result.refundAmount ?? 0)} (deducted ${formatPriceInINR(result.deductionAmount ?? 0)}). Unit released.`,
        { type: "success" },
      );
      setCancelTarget(null);
      await loadBookings();
      await refreshCommissionLedger();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to cancel booking";
      notify(message, { type: "error" });
    } finally {
      setCancelBusy(false);
    }
  };

  /** Creates a booking + Construction_Linked schedule for a real inventory unit (ops helper). */
  const handleSeedSampleBooking = async () => {
    try {
      // Always load from Supabase — Context may still show FakeRest seed IDs (prop-1)
      const propRes = await dataProvider.getList<{
        id: string;
        status?: string;
        base_price?: number;
      }>("properties", {
        pagination: { page: 1, perPage: 100 },
        sort: { field: "created_at", order: "DESC" },
        filter: {},
      });
      const liveProps = (propRes.data || []).filter((p) => isUuid(p.id));
      const prop =
        liveProps.find((p) => p.status === "Available") || liveProps[0];

      if (!prop) {
        notify(
          "No real inventory units yet. Add a property under Inventory first (demo seed units like prop-1 cannot be booked).",
          { type: "warning" },
        );
        return;
      }

      const contacts = await dataProvider.getList<{ id: string | number }>(
        "contacts",
        {
          pagination: { page: 1, perPage: 1 },
          sort: { field: "id", order: "ASC" },
          filter: {},
        },
      );
      const contactId = contacts.data?.[0]?.id;
      if (contactId == null) {
        notify("Create a contact first, then seed a booking", {
          type: "warning",
        });
        return;
      }

      const agreement = Number(prop.base_price) || 0;

      const bookingResult = await createBooking({
        contactId,
        propertyId: prop.id,
        agreementValue: agreement,
      });
      if (bookingResult.ok) {
        notify("Booking + payment schedule created", { type: "success" });
        await loadBookings();
        return;
      }
      notify(bookingResult.error || "Could not create booking", {
        type: "warning",
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to create booking";
      notify(message, { type: "error" });
    }
  };

  const handleDocumentsVerifiedToggle = async (
    bookingId: string,
    verified: boolean,
  ) => {
    setTogglingDocsId(bookingId);
    try {
      await dataProvider.update("bookings", {
        id: bookingId,
        data: { documents_verified: verified },
        previousData: { id: bookingId },
      });
      setBookings((prev) =>
        prev.map((b) =>
          b.id === bookingId ? { ...b, documents_verified: verified } : b,
        ),
      );
      notify(
        verified ? "Documents marked verified" : "Documents marked unverified",
        { type: "success" },
      );
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to update documents status";
      notify(message, { type: "error" });
    } finally {
      setTogglingDocsId(null);
    }
  };

  const handleDiscountDecision = async (
    bookingId: string,
    approve: boolean,
  ) => {
    const booking = bookings.find((b) => b.id === bookingId);
    if (!booking || booking.discount_status !== "pending") {
      notify("This discount is no longer pending approval", {
        type: "warning",
      });
      return;
    }

    setApprovingId(bookingId);
    try {
      const result = await approveBookingDiscount(bookingId, approve);
      if (!result.ok) {
        notify(result.error || "Could not update discount status", {
          type: "error",
        });
        return;
      }
      notify(approve ? "Discount approved" : "Discount rejected", {
        type: "success",
      });
      await loadBookings();
      await refreshCommissionLedger();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to update discount";
      notify(message, { type: "error" });
    } finally {
      setApprovingId(null);
    }
  };

  const handleExportForAccountant = () => {
    if (bookings.length === 0) {
      notify("No bookings to export", { type: "warning" });
      return;
    }

    const rows = bookings.map((b) => ({
      booking_id: b.id,
      booking_date: b.booking_date,
      unit_number: b.unit_number,
      project_name: b.project_name,
      buyer_name: b.buyer_name,
      list_price: Number(b.list_price) || 0,
      agreement_value: b.total_agreement_value,
      discount_amount: b.discount_amount ?? 0,
      discount_status: b.discount_status || "none",
      cp_agency: b.cp_agency || "",
      cp_percentage: b.cp_percentage ?? 0,
      cp_commission_amount: b.cp_commission_amount ?? 0,
      cp_payout_status: b.cp_payout_status || "pending",
      agent_name: b.agent_name || "",
      agent_commission_amount: b.agent_commission_amount ?? 0,
      agent_payout_status: b.agent_payout_status || "pending",
      total_paid: b.milestones.reduce(
        (sum, m) => sum + (Number(m.amount_paid) || 0),
        0,
      ),
      total_due: b.milestones.reduce(
        (sum, m) => sum + (Number(m.amount_due) || 0),
        0,
      ),
    }));

    return jsonExport(rows, {}, (err: unknown, csv: string) => {
      if (err) {
        const message =
          err instanceof Error ? err.message : "Failed to export CSV";
        notify(message, { type: "error" });
        return;
      }
      downloadCSV(csv, "bookings-commissions");
    });
  };

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
      <div className="border-b pb-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <FileText className="w-7 h-7 text-primary" />
            Post-Sales Milestone Payment Demands
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Live bookings and payment schedules. Download PDF, email (Postmark),
            or dispatch WhatsApp and mark milestones DEMAND_SENT.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Button variant="outline" size="sm" onClick={loadBookings}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportForAccountant}
          >
            <Download className="w-4 h-4 mr-1" /> Export for accountant
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={bulkBusy || loading}
            onClick={handleBulkDispatchOverdue}
          >
            <AlertTriangle className="w-4 h-4 mr-1" /> Dispatch all overdue
          </Button>
          <div className="flex items-center gap-1">
            <select
              className="h-8 rounded-md border border-input bg-transparent px-2 text-xs"
              value={bulkMilestoneName}
              onChange={(e) => setBulkMilestoneName(e.target.value)}
              disabled={bulkBusy || milestoneNames.length === 0}
            >
              <option value="">Milestone…</option>
              {milestoneNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              disabled={bulkBusy || !bulkMilestoneName}
              onClick={handleBulkDispatchMilestone}
            >
              <Send className="w-4 h-4 mr-1" /> Complete & dispatch
            </Button>
          </div>
          <Button size="sm" onClick={handleSeedSampleBooking}>
            Seed sample booking
          </Button>
        </div>
      </div>

      {loading && (
        <p className="text-sm text-muted-foreground">Loading bookings…</p>
      )}

      {!loading &&
        canApproveDiscounts &&
        pendingDiscountBookings.length > 0 && (
          <Card className="border-amber-200 dark:border-amber-900">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Percent className="w-5 h-5 text-amber-600" />
                Pending discount approvals
              </CardTitle>
              <CardDescription>
                Manager approval gates the discounted agreement value. Rejecting
                restores list price, recomputes CP and agent commissions, and
                rebuilds unpaid payment schedules (blocked if any payment was
                recorded).
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-xs text-left">
                <thead className="bg-muted/50 text-muted-foreground uppercase border-b">
                  <tr>
                    <th className="p-3">Unit</th>
                    <th className="p-3">Buyer</th>
                    <th className="p-3">List price</th>
                    <th className="p-3">Agreement value</th>
                    <th className="p-3">Discount</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {pendingDiscountBookings.map((booking) => {
                    const listPrice = Number(booking.list_price) || 0;
                    const discountAmount = Number(booking.discount_amount) || 0;
                    const discountPct =
                      listPrice > 0
                        ? ((discountAmount / listPrice) * 100).toFixed(1)
                        : "—";
                    const busy = approvingId === booking.id;

                    return (
                      <tr key={booking.id} className="hover:bg-muted/30">
                        <td className="p-3 font-semibold">
                          {booking.unit_number}
                        </td>
                        <td className="p-3">{booking.buyer_name}</td>
                        <td className="p-3">
                          {listPrice > 0 ? formatPriceInINR(listPrice) : "—"}
                        </td>
                        <td className="p-3">
                          {formatPriceInINR(booking.total_agreement_value)}
                        </td>
                        <td className="p-3">
                          {formatPriceInINR(discountAmount)}
                          {listPrice > 0 ? ` (${discountPct}%)` : ""}
                        </td>
                        <td className="p-3 text-right">
                          {booking.discount_status === "pending" && (
                            <div className="inline-flex gap-1">
                              <Button
                                size="sm"
                                className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                                disabled={busy}
                                onClick={() =>
                                  handleDiscountDecision(booking.id, true)
                                }
                              >
                                {busy ? "…" : "Approve"}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs text-red-600 border-red-200"
                                disabled={busy}
                                onClick={() =>
                                  handleDiscountDecision(booking.id, false)
                                }
                              >
                                Reject
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

      {!loading && bookings.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No bookings yet. Use &quot;Seed sample booking&quot; to create a
            Construction_Linked plan for an available unit, or insert via API.
          </CardContent>
        </Card>
      )}

      <div className="space-y-6">
        {bookings.map((booking) => (
          <Card key={booking.id} className="border-muted shadow-sm">
            <CardHeader className="bg-muted/30 pb-4 border-b">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <CardTitle className="text-xl font-bold text-foreground">
                      {booking.unit_number}
                    </CardTitle>
                    <Badge variant="outline" className="font-semibold">
                      {booking.project_name}
                    </Badge>
                    {booking.discount_status &&
                      booking.discount_status !== "none" &&
                      getDiscountBadge(booking.discount_status)}
                  </div>
                  <CardDescription className="flex flex-wrap items-center gap-3 mt-1 text-xs">
                    <span className="flex items-center gap-1 text-foreground font-medium">
                      <User className="w-3.5 h-3.5 text-primary" />{" "}
                      {booking.buyer_name} ({booking.buyer_phone || "no phone"}
                      {booking.buyer_email ? ` · ${booking.buyer_email}` : ""})
                    </span>
                    <span>
                      Agreement Value:{" "}
                      <strong className="text-primary font-bold">
                        {formatPriceInINR(booking.total_agreement_value)}
                      </strong>
                    </span>
                    {canApproveDiscounts && (
                      <span className="inline-flex items-center gap-2">
                        <Label
                          htmlFor={`docs-verified-${booking.id}`}
                          className="text-xs font-normal text-muted-foreground"
                        >
                          Documents verified
                        </Label>
                        <Switch
                          id={`docs-verified-${booking.id}`}
                          checked={Boolean(booking.documents_verified)}
                          disabled={togglingDocsId === booking.id}
                          onCheckedChange={(checked) =>
                            handleDocumentsVerifiedToggle(booking.id, checked)
                          }
                        />
                      </span>
                    )}
                  </CardDescription>
                </div>
                {canApproveDiscounts && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs text-red-600 border-red-200"
                    onClick={() => openCancelDialog(booking)}
                  >
                    <Ban className="w-3.5 h-3.5 mr-1" /> Cancel booking
                  </Button>
                )}
              </div>
            </CardHeader>

            <CardContent className="pt-4 p-0">
              <table className="w-full text-xs text-left">
                <thead className="bg-muted/50 text-muted-foreground uppercase border-b">
                  <tr>
                    <th className="p-3">Construction Milestone</th>
                    <th className="p-3">Due %</th>
                    <th className="p-3">Amount Due</th>
                    <th className="p-3">Status</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {booking.milestones.map((m) => {
                    const outstanding = milestoneOutstanding(m);
                    const interest = computeLateInterest({
                      outstanding,
                      annualPercent: lateInterestAnnualPercent,
                      dueDate: m.due_date,
                    });
                    return (
                      <tr key={m.id} className="hover:bg-muted/30">
                        <td className="p-3 font-semibold text-foreground">
                          {m.milestone_name}
                        </td>
                        <td className="p-3 font-bold">{m.due_percentage}%</td>
                        <td className="p-3 font-bold text-primary">
                          <div>{formatPriceInINR(m.amount_due)}</div>
                          {interest > 0 && (
                            <div className="text-[10px] font-normal text-amber-700 dark:text-amber-400 mt-0.5">
                              + {formatPriceInINR(interest)} late interest (
                              {lateInterestAnnualPercent}% p.a.)
                            </div>
                          )}
                        </td>
                        <td className="p-3">{getMilestoneBadge(m.status)}</td>
                        <td className="p-3 text-right">
                          {isDispatchableMilestone(m) ? (
                            <div className="inline-flex flex-wrap justify-end gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs"
                                onClick={() => handleDownloadPdf(booking, m)}
                              >
                                <Download className="w-3.5 h-3.5 mr-1" /> PDF
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs"
                                onClick={() => handleEmailDemand(booking, m)}
                              >
                                <Mail className="w-3.5 h-3.5 mr-1" /> Email
                              </Button>
                              <Button
                                size="sm"
                                className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs"
                                onClick={() => handleSendDemand(booking, m)}
                              >
                                <Send className="w-3.5 h-3.5 mr-1" /> Dispatch
                              </Button>
                            </div>
                          ) : m.status === "CANCELLED" ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-muted-foreground text-xs"
                              disabled
                            >
                              <Ban className="w-3.5 h-3.5 mr-1" /> Cancelled
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-emerald-600 text-xs"
                              disabled
                            >
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />{" "}
                              Cleared
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog
        open={cancelTarget != null}
        onOpenChange={(open) => {
          if (!open && !cancelBusy) setCancelTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel booking</DialogTitle>
            <DialogDescription>
              Releases unit {cancelTarget?.unit_number} back to Available and
              records the refund ledger. Unpaid milestones become CANCELLED.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label htmlFor="cancel-deduction">
                Deduction % of amount paid
              </Label>
              <Input
                id="cancel-deduction"
                type="number"
                min={0}
                max={100}
                step={1}
                value={cancelDeductionPercent}
                onChange={(e) =>
                  setCancelDeductionPercent(
                    Math.min(100, Math.max(0, Number(e.target.value) || 0)),
                  )
                }
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cancel-reason">Reason</Label>
              <Textarea
                id="cancel-reason"
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Optional cancellation reason"
              />
            </div>
            {cancelRefundPreview && (
              <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1">
                <div>
                  Amount paid:{" "}
                  <strong>
                    {formatPriceInINR(cancelRefundPreview.amountPaidTotal)}
                  </strong>
                </div>
                <div>
                  Deduction ({cancelRefundPreview.deductionPercent}%):{" "}
                  <strong>
                    {formatPriceInINR(cancelRefundPreview.deductionAmount)}
                  </strong>
                </div>
                <div>
                  Refund:{" "}
                  <strong className="text-primary">
                    {formatPriceInINR(cancelRefundPreview.refundAmount)}
                  </strong>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={cancelBusy}
              onClick={() => setCancelTarget(null)}
            >
              Keep booking
            </Button>
            <Button
              variant="destructive"
              disabled={cancelBusy}
              onClick={handleConfirmCancel}
            >
              {cancelBusy ? "Cancelling…" : "Confirm cancel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DemandLetterGenerator;
